import { AlertCircleIcon } from "lucide-react";

import { Alert, AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { AppError } from "@/shared/lib/utils/errors";

type ErrorVariant = "alert" | "modal";

interface ErrorProps {
  title: string;
  error: AppError | Error;
  variant?: ErrorVariant;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onRetry?: () => void;
}

export function ErrorBody({ error }: { error: AppError | Error }) {
  if (error instanceof AppError && error.details.length > 0) {
    return error.details.length === 1 ? (
      <p>{error.details[0].message}</p>
    ) : (
      <ul className="list-inside list-disc">
        {error.details.map((detail, index) => (
          <li key={index}>{detail.message}</li>
        ))}
      </ul>
    );
  }

  return <p>{error.message}</p>;
}

export default function Error({ title, error, variant = "alert", open, onOpenChange, onRetry }: ErrorProps) {
  const status = error instanceof AppError ? error.status : undefined;

  if (variant === "modal") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent showCloseButton>
          <DialogHeader>
            <div className="flex flex-col items-center gap-4 pb-2 text-center">
              <div className="flex size-14 items-center justify-center bg-destructive/10">
                <AlertCircleIcon className="size-7 text-destructive" />
              </div>
              <div className="flex flex-col gap-1.5">
                <DialogTitle className="text-destructive">{title}</DialogTitle>
                <p className="text-sm text-muted-foreground">An error occurred. Review the details below and try again, or contact support if the problem persists.</p>
              </div>
            </div>
            <div className="relative border-l-2 border-destructive/40 bg-muted py-3 pl-4 pr-3 text-sm">
              {status !== undefined && <span className="absolute right-3 top-3 font-mono text-xs font-semibold text-destructive">HTTP {status}</span>}
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Error detail</p>
              <div className="text-sm text-foreground">
                <ErrorBody error={error} />
              </div>
            </div>
          </DialogHeader>
          <DialogFooter>
            {onRetry && (
              <Button variant="default" onClick={onRetry}>
                Try again
              </Button>
            )}
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Alert variant="destructive">
      <AlertCircleIcon />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {(status !== undefined || onRetry) && (
          <AlertAction className="flex flex-col items-end gap-1.5">
            {status !== undefined && <span className="font-mono text-xs font-semibold text-destructive">HTTP {status}</span>}
            {onRetry && (
              <Button variant="ghost" size="sm" className="h-auto px-0 text-xs font-semibold text-destructive hover:bg-transparent hover:underline" onClick={onRetry}>
                Try again
              </Button>
            )}
          </AlertAction>
        )}
        <ErrorBody error={error} />
      </AlertDescription>
    </Alert>
  );
}
